/**
 * How diagnosis-code validity is shown. The judgment is the backend's
 * (`diagnosis_code_validity.py`): a code is invalid when the ICD-10-CM release
 * in force on the record's date (an OASIS's coding date — M0090, else the
 * visit date — a visit date, or a claim's Through date) does not list it, or
 * lists it only as a non-billable header. On a claim, that
 * release is the one for the claim's Through date.
 *
 * Validity applies to every seat, secondaries included, because Medicare edits
 * every code on the claim. It is not principal-diagnosis fitness: the PDX-only
 * rules (manifestation, ECOI, unspecified, no clinical group) stay on the
 * primary.
 */
import type { DiagnosisCodeInvalidReason } from '../services/api/types.gen';
import { getRefusedDiagnoses, type RefusedDiagnosis } from './apiErrorDetail';

const ROW_MESSAGES: Record<DiagnosisCodeInvalidReason, string> = {
  not_billable: 'This code is not billable. Use a more specific code.',
  not_listed: 'This code is not valid for this date.',
};

/** The warning shown on a diagnosis row, or null when the code is valid. */
export function diagnosisCodeValidityMessage(
  reason: DiagnosisCodeInvalidReason | null | undefined
): string | null {
  return reason ? ROW_MESSAGES[reason] : null;
}

/**
 * The claim-readiness line that names the code. A claim's codes are judged
 * against the ICD-10-CM release for its Through date.
 */
export function invalidDiagnosisCodeClaimMessage(
  code: string,
  reason: DiagnosisCodeInvalidReason
): string {
  return reason === 'not_billable'
    ? `Diagnosis code ${code} is not billable for this claim's Through date. Use a more specific code.`
    : `Diagnosis code ${code} is not valid for this claim's Through date.`;
}

const REFUSED_REASON_LABELS: Record<RefusedDiagnosis['reason'], string> = {
  not_billable: 'not billable',
  not_listed: 'not valid for this date',
};

/**
 * The toast for a Roger Docs 422 that refused codes as not valid for a claim,
 * or null when the error is anything else (callers then show their generic
 * copy). Built from the structured items only — Docs' own `message` names its
 * reason enums and is never shown.
 *
 *   refusedDiagnosesMessage(error, "Can't save.")
 *   → "Can't save. Fix these codes: I42.0 (not billable), S23.420A (not valid for this date)."
 */
export function refusedDiagnosesMessage(
  error: unknown,
  lead: string
): string | null {
  const refused = getRefusedDiagnoses(error);
  if (refused.length === 0) return null;
  const list = refused
    .map(({ code, reason }) => `${code} (${REFUSED_REASON_LABELS[reason]})`)
    .join(', ');
  return `${lead} Fix these codes: ${list}.`;
}
